import { cn } from '@/utlis/cn'
import { MusePeer } from '@/services/RoomContextMusePeer'
import PinIcon from '@/CustomIcons/PinIcon'
import { isReordering, sessionViewModeState, useViewMode } from './useViewMode'
import { Button } from '@/UIComponents2/Button'
import DotsMenuIcon from '@/CustomIcons/DotsMenuIcon'
import { useTranslation } from 'react-i18next'
import { useUserContext } from '@/UserContext'
import { useRoomContext } from '@/RoomContext'
import { SimpleMenu } from '@/UIComponents2/SimpleMenu'
import useAnalytics from '@/services/useAnalytics'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useRoomActions from '@/services/useRoomActions'
import useRemoteDesktopControlPermissionActions from '@/services/useRemoteDesktopControlPermissionActions'
import { sessionSettingsTrigger } from '../../SessionPage'
import { useAppContext } from '@/AppContext'
import { useState } from 'react'
import useMiscRoomActions from '@/services/useMiscRoomActions'
import { env } from '@/services/EnvironmentVariables'
import { overlayBgClasses } from '@/components/OverlayControlButton'
import useRoomViews from '@/services/useRoomViews'

type VideoOptionsOverlayProps = {
    peer: MusePeer | 'me'
    pinned?: boolean
    kind: 'camera' | 'screen'
    className?: string
    hidePin?: boolean
    size?: 'small' | 'regular'
}
export function VideoOptionsOverlay(props: VideoOptionsOverlayProps) {
    const size = props.size ?? 'regular'
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    function handlePin() {
        if (!props.pinned) {
            if (props.peer !== 'me') {
                if (
                    sessionViewModeState.mode.galleryHidden &&
                    !sessionViewModeState.mode.fullscreen &&
                    env.IS_ELECTRON
                ) {
                    sessionViewModeState.setGalleryHidden(false)
                }
                sessionViewModeState.setGalleryLayout({
                    name: 'pinned',
                    pinnedVideo: { peerSessionId: props.peer.sessionId, kind: props.kind },
                    page: sessionViewModeState.currentGalleryPage,
                })
            }
        } else {
            sessionViewModeState.setGalleryLayout({
                name: 'grid',
                page: sessionViewModeState.currentGalleryPage,
                reordering: false,
            })
        }
    }

    const showScreenPinMessage =
        props.kind === 'screen' &&
        !props.pinned &&
        (!sessionViewModeState.mode.galleryHidden ||
            (sessionViewModeState.mode.galleryHidden && env.IS_ELECTRON && !sessionViewModeState.mode.fullscreen))

    const showLittlePinButton =
        !props.hidePin && props.peer !== 'me' && (props.kind === 'camera' || (props.kind === 'screen' && props.pinned))

    const bg = cn(
        overlayBgClasses,
        'p-[8px] border-muse-toplit rounded-full shadow-glow',
        isMenuOpen ? 'opacity-100 bg-black/40' : 'opacity-0'
    )
    const position = size === 'small' ? 'top-1 right-1' : 'top-[16px] right-[16px]'
    const circleButtonSize = 'w-[36px] h-[36px]'

    return (
        <>
            <div className={cn('absolute flex gap-1 items-center justify-center z-11', position, props.className)}>
                {showLittlePinButton && (
                    <Button
                        onClick={handlePin}
                        className={cn(
                            bg,
                            circleButtonSize,
                            'flex items-center justify-center',
                            props.pinned && 'opacity-100 bg-black/20 backdrop-blur-[8px]'
                        )}
                    >
                        <PinIcon outlineOnly={!props.pinned} style={{ fontSize: '1.3rem' }} />
                    </Button>
                )}
                <Menu
                    peerSessionId={props.peer !== 'me' ? props.peer.sessionId : ''}
                    isMe={props.peer === 'me'}
                    isPinned={props.pinned ?? false}
                    kind={props.kind}
                    onOpenChange={(open) => setIsMenuOpen(open)}
                >
                    <Button className={cn(bg, circleButtonSize, 'flex items-center justify-center')}>
                        <DotsMenuIcon style={{ fontSize: '1.3rem' }} />
                    </Button>
                </Menu>
            </div>
            {showScreenPinMessage && (
                <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Button onClick={handlePin} className={cn(bg, 't-base-medium rounded-4 py-[10px] px-6')}>
                        View Screen Share
                    </Button>
                </div>
            )}
        </>
    )
}

type MenuProps = {
    isMe: boolean
    peerSessionId: string
    kind: 'camera' | 'screen'
    isPinned: boolean
    children?: React.ReactNode
    onOpenChange?: (open: boolean) => void
}
function Menu(props: MenuProps) {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const [roomState, roomDispatch] = useRoomContext()
    const [appState, appDispatch] = useAppContext()
    const { startRemovePeer } = useRoomActions()
    const { logUserActivityWithContext } = useAnalytics()
    const {
        revoke: desktopControlRevoke,
        isSessionIdControllingMyDesktop,
        stop: desktopControlStop,
        showRemoteDesktopControlQuery,
    } = useRemoteDesktopControlPermissionActions()
    const { unhideAllVidoesFromGallery } = useMiscRoomActions()
    const viewMode = useViewMode()
    const { presentingPinnedPeerSessionId } = useRoomViews()

    const isListenOnly = userState.clientType === 'listenonly'
    const isControllingMyDesktop = props.isMe ? false : isSessionIdControllingMyDesktop(props.peerSessionId)
    const virtualBackgroundEnabled = !!appState.appSettings.canaryFeatures['virtual-backgrounds']
    const canRemoveUsers = roomState.isHost

    const analyticsProperties = {
        ...props,
        componentSource: 'VideoTopBar',
    }

    function onRequestScreenControl() {
        showRemoteDesktopControlQuery()
    }

    function onStopScreenControl() {
        const peerSessionId = props.peerSessionId
        if (peerSessionId) {
            desktopControlStop({ peerSessionId })
        }
    }

    const list: any[] = [
        {
            show: isControllingMyDesktop,
            label: t('Stop screen control'),
            onClick: () => {
                desktopControlRevoke()
                logUserActivityWithContext(AnalyticsEvents.CLICKED_STOP_SCREEN_CONTROL_BUTTON, analyticsProperties)
            },
        },
        {
            show: !props.isMe && !props.isPinned,
            label: t('Pin Video'),
            onClick: () => {
                sessionViewModeState.setGalleryLayout({
                    name: 'pinned',
                    pinnedVideo: { peerSessionId: props.peerSessionId, kind: props.kind },
                    page: sessionViewModeState.currentGalleryPage,
                })
                logUserActivityWithContext(AnalyticsEvents.CLICKED_PIN_VIDEO_BUTTON, analyticsProperties)
            },
        },
        {
            show: !props.isMe && props.isPinned,
            label: t('Unpin Video'),
            onClick: () => {
                sessionViewModeState.setGalleryLayout({
                    name: 'grid',
                    page: sessionViewModeState.currentGalleryPage,
                    reordering: false,
                })
                logUserActivityWithContext(AnalyticsEvents.CLICKED_UNPIN_VIDEO_BUTTON, analyticsProperties)
            },
        },
        {
            show: props.isMe,
            label: t('My video settings'),
            onClick: () => {
                sessionSettingsTrigger.trigger({ tab: 'video' })
                logUserActivityWithContext(AnalyticsEvents.CLICKED_MY_VIDEO_SETTINGS_BUTTON, analyticsProperties)
            },
        },
        {
            show: props.isMe && virtualBackgroundEnabled,
            label: (
                <div className="flex gap-2">
                    <span>{t('Select Virtual Background')}</span>
                    <p className="text-muse-yellow text-[10px] align-super h-fit">{t('Beta')}</p>
                </div>
            ),
            onClick: () => {
                logUserActivityWithContext(AnalyticsEvents.VIRTUAL_BACKGROUND_MODAL_OPENED, analyticsProperties)
                appDispatch({
                    type: 'update_app_state',
                    value: { showChooseVirtualBackgroundModal: true },
                })
            },
        },
        {
            show: true,
            label: appState.appSettings.noShowEmptyCamerasOnGallery
                ? t('Show empty cameras on gallery')
                : t("Don't show empty cameras on gallery"),
            onClick: () => {
                appDispatch({
                    type: 'update_app_settings',
                    value: { noShowEmptyCamerasOnGallery: !appState.appSettings.noShowEmptyCamerasOnGallery },
                })
            },
        },
        {
            show: !props.isMe && !isListenOnly,
            label: canRemoveUsers ? t('Manage collaborators') : t('View collaborators'),
            onClick: () => {
                sessionSettingsTrigger.trigger({ tab: 'session' })
                logUserActivityWithContext(AnalyticsEvents.CLICKED_MANAGE_COLLABORATORS_BUTTON, analyticsProperties)
            },
        },
        {
            show:
                presentingPinnedPeerSessionId === props.peerSessionId &&
                roomState.remoteDesktopControlStatus?.status === 'allowed',
            label: t('Stop screen control'),
            onClick: () => {
                onStopScreenControl()
            },
        },
        {
            show:
                presentingPinnedPeerSessionId === props.peerSessionId &&
                roomState.remoteDesktopControlStatus?.status !== 'allowed',
            label: t('Request screen control'),
            onClick: () => {
                onRequestScreenControl()
            },
        },
        {
            show: true,
            label: t('Hide video from gallery'),
            onClick: () => {
                const videoHiddenSessionIdsNext = [...roomState.videoHiddenSessionIds]
                if (props.isMe) {
                    videoHiddenSessionIdsNext.push('_me')
                } else {
                    videoHiddenSessionIdsNext.push(props.peerSessionId)
                }
                roomDispatch({
                    type: 'update_room_context',
                    value: { videoHiddenSessionIds: videoHiddenSessionIdsNext },
                })
            },
        },
        {
            show: roomState.videoHiddenSessionIds.length > 0,
            label: t('Unhide all videos from gallery'),
            onClick: () => {
                unhideAllVidoesFromGallery()
            },
        },
        {
            show: !props.isMe && canRemoveUsers,
            label: t('Remove from session'),
            className: 'text-muse-danger',
            onClick: () => {
                startRemovePeer(props.peerSessionId)
                logUserActivityWithContext(AnalyticsEvents.CLICKED_REMOVE_USER_BUTTON, analyticsProperties)
            },
        },
        {
            show: !isReordering(viewMode),
            label: t('Reorder videos'),
            onClick: () => {
                sessionViewModeState.toggleReorderVideoMode()
            },
        },
        {
            show: isReordering(viewMode),
            label: t('Done reordering videos'),
            onClick: () => {
                sessionViewModeState.toggleReorderVideoMode()
            },
        },
    ].filter((item) => item.show)

    if (list.length === 0) {
        return null
    }

    return (
        <SimpleMenu items={list} className="max-w-[300px]" onOpenChange={props.onOpenChange}>
            {props.children}
        </SimpleMenu>
    )
}
